import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Check, Globe, Loader2, Pencil, Star, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NativeSelect } from '../components/ui/native-select';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../hooks/useConfirm';

const DEFAULT_CONFIG = {
  initial_funds: 100000,
  max_positions: 5,
  entry: 'next_open',
  stop_loss_pct: '',
  take_profit_pct: '',
  trailing_stop_pct: '',
  max_holding_days: '',
  atr_stop_multiplier: '',
  atr_stop_mode: 'fixed',
  atr_stop_period: 14,
  position_sizing: 'equal_weight',
  position_size_value: 0,
  vol_lookback: 14,
  vol_atr_multiplier: 2.0,
};

function daysAgo(n) {
  return new Date(Date.now() - n * 24 * 3600 * 1000).toISOString().split('T')[0];
}
const TODAY    = daysAgo(0);
const THREE_YR = daysAgo(3 * 365);

function fmtDate(s) { return s ? s.slice(0, 10) : ''; }

function configSummary(cfg) {
  if (!cfg) return '—';
  const parts = [cfg.position_sizing];
  if (cfg.stop_loss_pct)     parts.push(`SL:${cfg.stop_loss_pct}%`);
  if (cfg.take_profit_pct)   parts.push(`TP:${cfg.take_profit_pct}%`);
  if (cfg.trailing_stop_pct) parts.push(`TS:${cfg.trailing_stop_pct}%`);
  if (cfg.atr_stop_multiplier) parts.push(`ATR:${cfg.atr_stop_multiplier}×${cfg.atr_stop_mode === 'trailing' ? 'T' : 'F'}`);
  if (cfg.max_holding_days)  parts.push(`${cfg.max_holding_days}d`);
  parts.push(`${cfg.max_positions}pos`);
  return parts.join(' · ');
}

// ── Shared label+input row ────────────────────────────────────────────────────

function FieldRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

// ── Trade Config Form ─────────────────────────────────────────────────────────

function TradeConfigForm({ initial, onSave, onCancel, saving }) {
  const [name,   setName]   = useState(initial?.name   || '');
  const [config, setConfig] = useState(initial?.config ? {
    ...DEFAULT_CONFIG, ...initial.config,
    stop_loss_pct:     initial.config.stop_loss_pct     ?? '',
    take_profit_pct:   initial.config.take_profit_pct   ?? '',
    trailing_stop_pct: initial.config.trailing_stop_pct ?? '',
    max_holding_days:  initial.config.max_holding_days  ?? '',
    atr_stop_multiplier: initial.config.atr_stop_multiplier ?? '',
    atr_stop_mode:     initial.config.atr_stop_mode     ?? 'fixed',
    atr_stop_period:   initial.config.atr_stop_period   ?? 14,
    max_positions:     initial.config.max_positions ?? 5
  } : { ...DEFAULT_CONFIG });
  const [err, setErr] = useState('');

  const set = (key, val) => setConfig(p => ({ ...p, [key]: val }));
  const showVolParams  = config.position_sizing === 'volatility';
  const showSizeValue  = config.position_sizing === 'fixed_amount' || config.position_sizing === 'fixed_pct';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setErr('');
    try {
      await onSave({
        name: name.trim(),
        config: {
          ...config,
          stop_loss_pct:     config.stop_loss_pct     ? parseFloat(config.stop_loss_pct)     : null,
          take_profit_pct:   config.take_profit_pct   ? parseFloat(config.take_profit_pct)   : null,
          trailing_stop_pct: config.trailing_stop_pct ? parseFloat(config.trailing_stop_pct) : null,
          max_holding_days:  config.max_holding_days  ? parseInt(config.max_holding_days, 10) : null,
          atr_stop_multiplier: config.atr_stop_multiplier ? parseFloat(config.atr_stop_multiplier) : null,
          atr_stop_mode:     config.atr_stop_mode || 'fixed',
          atr_stop_period:   config.atr_stop_period ? parseInt(config.atr_stop_period, 10) : 14,
          max_positions:     config.max_positions  ? parseInt(config.max_positions, 10) : null,
        },
      });
    } catch (e) {
      setErr(e.message || 'Failed to save');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 bg-surface-card flex flex-col gap-4 mb-4">
      <FieldRow label="Name">
        <Input
          type="text"
          placeholder="e.g. Tight Stop Equal Weight"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />
      </FieldRow>

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">Entry Rules</div>
        <FieldRow label="Maximum Positions">
          <Input type="number" step="1" placeholder="e.g. 5" value={config.max_positions} onChange={e => set('max_positions', e.target.value)} />
        </FieldRow>
      </div>
      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">Exit Rules</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRow label="Stop Loss %">
            <Input type="number" step="0.1" placeholder="e.g. 5" value={config.stop_loss_pct} onChange={e => set('stop_loss_pct', e.target.value)} />
          </FieldRow>
          <FieldRow label="Take Profit %">
            <Input type="number" step="0.1" placeholder="e.g. 15" value={config.take_profit_pct} onChange={e => set('take_profit_pct', e.target.value)} />
          </FieldRow>
          <FieldRow label="Trailing Stop %">
            <Input type="number" step="0.1" placeholder="e.g. 8" value={config.trailing_stop_pct} onChange={e => set('trailing_stop_pct', e.target.value)} />
          </FieldRow>
          <FieldRow label="Max Holding Days">
            <Input type="number" min="1" placeholder="e.g. 20" value={config.max_holding_days} onChange={e => set('max_holding_days', e.target.value)} />
          </FieldRow>
          <FieldRow label="ATR Stop Multiplier">
            <Input type="number" step="0.1" placeholder="e.g. 2.5 (blank = off)" value={config.atr_stop_multiplier} onChange={e => set('atr_stop_multiplier', e.target.value)} />
          </FieldRow>
          {config.atr_stop_multiplier != null && config.atr_stop_multiplier > 0 && <>
            <FieldRow label="ATR Stop Period">
              <Input type="number" min="2" placeholder="e.g. 14" value={config.atr_stop_period} onChange={e => set('atr_stop_period', e.target.value)} />
            </FieldRow>
            <FieldRow label="ATR Stop Mode">
              <NativeSelect value={config.atr_stop_mode} onChange={e => set('atr_stop_mode', e.target.value)}>
                <option value="fixed">Fixed (from entry)</option>
                <option value="trailing">Trailing (from peak)</option>
              </NativeSelect>
            </FieldRow>
          </>}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">Position Sizing</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRow label="Method">
            <NativeSelect value={config.position_sizing} onChange={e => set('position_sizing', e.target.value)}>
              <option value="equal_weight">Equal Weight</option>
              <option value="fixed_amount">Fixed Amount (₹)</option>
              <option value="fixed_pct">Fixed % of Portfolio</option>
              <option value="volatility">Volatility (ATR)</option>
            </NativeSelect>
          </FieldRow>
          {showSizeValue && (
            <FieldRow label={config.position_sizing === 'fixed_amount' ? 'Amount (₹)' : 'Portfolio %'}>
              <Input type="number" step="0.1" value={config.position_size_value} onChange={e => set('position_size_value', parseFloat(e.target.value))} />
            </FieldRow>
          )}
          {showVolParams && (<>
            <FieldRow label="Risk % per Trade">
              <Input type="number" step="0.1" value={config.position_size_value || 1} onChange={e => set('position_size_value', parseFloat(e.target.value))} />
            </FieldRow>
            <FieldRow label="ATR Period">
              <Input type="number" min="2" value={config.vol_lookback} onChange={e => set('vol_lookback', parseInt(e.target.value, 10))} />
            </FieldRow>
            <FieldRow label="ATR Multiplier">
              <Input type="number" step="0.1" value={config.vol_atr_multiplier} onChange={e => set('vol_atr_multiplier', parseFloat(e.target.value))} />
            </FieldRow>
          </>)}
        </div>
      </div>

      {err && <p className="text-xs text-destructive">{err}</p>}

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : initial ? 'Update' : 'Add Trade Config'}
        </Button>
      </div>
    </form>
  );
}

// ── Trade Config Grid Form (parameter sweep) ──────────────────────────────────

const GRID_MAX = 100;

function parseSweepList(text, isInt) {
  const tokens = text.split(',').map(t => t.trim()).filter(t => t.length > 0);
  if (tokens.length === 0) return null;
  return tokens.map(t => {
    if (t.toLowerCase() === 'none' || t.toLowerCase() === 'off') return null;
    return isInt ? parseInt(t, 10) : parseFloat(t);
  });
}

function TradeConfigGridForm({ onSave, onCancel, saving }) {
  const [namePrefix, setNamePrefix] = useState('Grid');
  const [base, setBase] = useState({ ...DEFAULT_CONFIG });
  const [sweepSL, setSweepSL] = useState('3, 5, 8');
  const [sweepTP, setSweepTP] = useState('10, 15, 20');
  const [sweepTS, setSweepTS] = useState('');
  const [sweepMH, setSweepMH] = useState('');
  const [err, setErr] = useState('');

  const setB = (key, val) => setBase(p => ({ ...p, [key]: val }));
  const showVolParams  = base.position_sizing === 'volatility';
  const showSizeValue  = base.position_sizing === 'fixed_amount' || base.position_sizing === 'fixed_pct';

  const ranges = {
    stop_loss_pct:      parseSweepList(sweepSL, false),
    take_profit_pct:    parseSweepList(sweepTP, false),
    trailing_stop_pct:  parseSweepList(sweepTS, false),
    max_holding_days:   parseSweepList(sweepMH, true),
  };
  const gridSize = Object.values(ranges).reduce((n, list) => n * (list ? list.length : 1), 1);
  const tooBig = gridSize > GRID_MAX;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (gridSize < 1) { setErr('Add at least one value to sweep'); return; }
    if (tooBig) { setErr(`This would create ${gridSize} configs — narrow the ranges to ${GRID_MAX} or fewer`); return; }
    setErr('');
    const param_ranges = {};
    Object.entries(ranges).forEach(([k, v]) => { if (v) param_ranges[k] = v; });
    try {
      await onSave({
        name_prefix: namePrefix.trim() || 'Grid',
        base_config: {
          ...base,
          stop_loss_pct:     null,
          take_profit_pct:   null,
          trailing_stop_pct: null,
          max_holding_days:  null,
          atr_stop_multiplier: base.atr_stop_multiplier ? parseFloat(base.atr_stop_multiplier) : null,
          atr_stop_mode:     base.atr_stop_mode || 'fixed',
          atr_stop_period:   base.atr_stop_period ? parseInt(base.atr_stop_period, 10) : 14,
          max_positions:     base.max_positions ? parseInt(base.max_positions, 10) : null,
        },
        param_ranges,
      });
    } catch (e) {
      setErr(e.message || 'Failed to generate grid');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 bg-surface-card flex flex-col gap-4 mb-4">
      <FieldRow label="Name Prefix">
        <Input type="text" placeholder="e.g. Grid" value={namePrefix} onChange={e => setNamePrefix(e.target.value)} />
      </FieldRow>

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">
          Parameter Sweep <span className="font-normal text-muted-foreground/70">(comma-separated; use "none" to include a disabled variant)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRow label="Stop Loss % values">
            <Input type="text" placeholder="e.g. 3, 5, 8" value={sweepSL} onChange={e => setSweepSL(e.target.value)} />
          </FieldRow>
          <FieldRow label="Take Profit % values">
            <Input type="text" placeholder="e.g. 10, 15, 20" value={sweepTP} onChange={e => setSweepTP(e.target.value)} />
          </FieldRow>
          <FieldRow label="Trailing Stop % values">
            <Input type="text" placeholder="e.g. none, 5, 8" value={sweepTS} onChange={e => setSweepTS(e.target.value)} />
          </FieldRow>
          <FieldRow label="Max Holding Days values">
            <Input type="text" placeholder="e.g. none, 10, 20" value={sweepMH} onChange={e => setSweepMH(e.target.value)} />
          </FieldRow>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">Base Settings (fixed across the grid)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldRow label="Maximum Positions">
            <Input type="number" step="1" value={base.max_positions} onChange={e => setB('max_positions', e.target.value)} />
          </FieldRow>
          <FieldRow label="Entry">
            <NativeSelect value={base.entry} onChange={e => setB('entry', e.target.value)}>
              <option value="next_open">Next Open</option>
              <option value="signal_close">Signal Close</option>
            </NativeSelect>
          </FieldRow>
          <FieldRow label="Position Sizing">
            <NativeSelect value={base.position_sizing} onChange={e => setB('position_sizing', e.target.value)}>
              <option value="equal_weight">Equal Weight</option>
              <option value="fixed_amount">Fixed Amount (₹)</option>
              <option value="fixed_pct">Fixed % of Portfolio</option>
              <option value="volatility">Volatility (ATR)</option>
            </NativeSelect>
          </FieldRow>
          {showSizeValue && (
            <FieldRow label={base.position_sizing === 'fixed_amount' ? 'Amount (₹)' : 'Portfolio %'}>
              <Input type="number" step="0.1" value={base.position_size_value} onChange={e => setB('position_size_value', parseFloat(e.target.value))} />
            </FieldRow>
          )}
          {showVolParams && (<>
            <FieldRow label="Risk % per Trade">
              <Input type="number" step="0.1" value={base.position_size_value || 1} onChange={e => setB('position_size_value', parseFloat(e.target.value))} />
            </FieldRow>
            <FieldRow label="ATR Period">
              <Input type="number" min="2" value={base.vol_lookback} onChange={e => setB('vol_lookback', parseInt(e.target.value, 10))} />
            </FieldRow>
            <FieldRow label="ATR Multiplier">
              <Input type="number" step="0.1" value={base.vol_atr_multiplier} onChange={e => setB('vol_atr_multiplier', parseFloat(e.target.value))} />
            </FieldRow>
          </>)}
        </div>
      </div>

      <p className={`text-xs ${tooBig ? 'text-destructive' : 'text-muted-foreground'}`}>
        This will create {gridSize} trade config{gridSize === 1 ? '' : 's'}.
        {tooBig && ` Narrow the ranges to ${GRID_MAX} or fewer.`}
      </p>

      {err && <p className="text-xs text-destructive">{err}</p>}

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || tooBig}>
          {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Generating…</> : `Generate ${gridSize} Configs`}
        </Button>
      </div>
    </form>
  );
}

// ── Pair Matrix (per-screener × trade-config exclusions) ──────────────────────

function PairMatrix({
  screeners, tradeConfigs, isExcluded, onToggle, readOnly,
  isDefault, onSetDefault, fallbackEnabled,
}) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="border-b border-border bg-surface-card">
            <tr>
              <th className="px-3 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Screener</th>
              {tradeConfigs.map(tc => (
                <th key={tc.id} title={tc.name} className="px-3 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap max-w-[10rem] truncate">
                  {tc.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {screeners.map(s => (
              <tr key={s.id} className="border-b border-border/40 hover:bg-surface-hover transition-colors">
                <td className="px-3 py-2 text-sm font-medium text-foreground whitespace-nowrap">{s.screener_name}</td>
                {tradeConfigs.map(tc => {
                  const excluded = isExcluded(s.id, tc.id);
                  const isDflt   = isDefault?.(s.id, tc.id);
                  return (
                    <td key={tc.id} className="px-3 py-2">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          checked={!excluded}
                          disabled={readOnly}
                          onChange={() => onToggle(s.id, tc.id, excluded)}
                          title={excluded ? 'Excluded — click to enable' : 'Active — click to exclude'}
                        />
                        {/* An excluded pair cannot be the fallback — the two
                            settings would contradict each other. */}
                        <button
                          type="button"
                          disabled={readOnly || excluded || !fallbackEnabled}
                          onClick={() => onSetDefault(s.id, tc.id, isDflt)}
                          aria-label={isDflt ? 'Clear fallback pair' : 'Make this the fallback pair'}
                          title={
                            !fallbackEnabled ? 'Turn on "Sit out when no pair qualifies" to use a fallback pair'
                              : excluded ? 'Re-enable this pair before making it the fallback'
                                : isDflt ? 'Fallback pair — click to clear'
                                  : 'Make this the fallback pair'
                          }
                          className={`transition-colors ${
                            (excluded || !fallbackEnabled) ? 'opacity-20 cursor-not-allowed'
                              : isDflt ? 'text-amber-500'
                                : 'text-muted-foreground/30 hover:text-amber-500'
                          }`}
                        >
                          <Star className="w-3.5 h-3.5" fill={isDflt ? 'currentColor' : 'none'} />
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Rank options for backtest / schedule ──────────────────────────────────────

const RANK_OPTIONS = [
  { value: 'sharpe',       label: 'Sharpe Ratio',  desc: 'Return per unit of total volatility' },
  { value: 'sortino',      label: 'Sortino Ratio', desc: 'Return per unit of downside volatility' },
  { value: 'calmar',       label: 'Calmar',        desc: 'Annualised return ÷ Max Drawdown' },
  { value: 'total_return', label: 'Total Return',  desc: 'Raw % gain over training period' },
  { value: 'win_rate',     label: 'Win Rate',      desc: '% of trades that were profitable' },
];

function RankSelector({ value, onChange, name }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {RANK_OPTIONS.map(opt => (
        <label
          key={opt.value}
          className={`flex flex-col gap-0.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${value === opt.value ? 'border-primary bg-primary/5 text-foreground' : 'border-border hover:border-border/80'}`}
        >
          <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} className="sr-only" />
          <span className="text-xs font-semibold text-foreground">{opt.label}</span>
          <span className="text-[11px] text-muted-foreground">{opt.desc}</span>
        </label>
      ))}
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, action, children }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {action && <div className="self-start sm:self-auto">{action}</div>}
      </div>
      {children}
    </section>
  );
}

// ── Data table ────────────────────────────────────────────────────────────────

function DataTable({ headers, children, empty }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="border-b border-border bg-surface-card">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {children}
          </tbody>
        </table>
      </div>
      {empty && <div className="px-4 py-6 text-sm text-muted-foreground text-center">{empty}</div>}
    </div>
  );
}

const TABS = [
  { key: 'config',      label: 'Config' },
  { key: 'schedule',    label: 'Re-rank' },
  { key: 'backtesting', label: 'Backtesting' },
];


// ── Main page ─────────────────────────────────────────────────────────────────

export default function HelmDetail({tab}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin;
  const [activeTab, setActiveTab] = useState(tab ?? 'config');

  const [helm,         setHelm]         = useState(null);
  const [screeners,    setScreeners]    = useState([]);
  const [allScreeners, setAllScreeners] = useState([]);
  const [tradeConfigs, setTradeConfigs] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  const [editingName,  setEditingName]  = useState(false);
  const [nameInput,    setNameInput]    = useState('');
  const [savingName,   setSavingName]   = useState(false);

  const [showAddScreener, setShowAddScreener]   = useState(false);
  const [newScreenerId,   setNewScreenerId]     = useState('');
  const [newScreenerLabel, setNewScreenerLabel] = useState('');
  const [addingScreener,  setAddingScreener]   = useState(false);

  const [showAddTC, setShowAddTC] = useState(false);
  const [editingTC, setEditingTC] = useState(null);
  const [savingTC,  setSavingTC]  = useState(false);

  const [showGridForm, setShowGridForm] = useState(false);
  const [savingGrid,   setSavingGrid]   = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const [pairExclusions, setPairExclusions] = useState([]);
  const { confirm, confirmDialog } = useConfirm();

  const [wfForm, setWfForm] = useState({
    wf_start:      THREE_YR,
    wf_end:        TODAY,
    training_days: 90,
    interval_days: 30,
    top_k:         1,
    initial_funds: 100000,
    rank_by:       'calmar',
  });
  const [wfRuns,    setWfRuns]    = useState([]);
  const [running,   setRunning]   = useState(false);

  // Saved run configs ("master list"). selectedConfigId is the config the form
  // was loaded from; wfDirty tracks edits made since, so a run is only tagged
  // with a saved config when the form still matches it.
  const [wfConfigs,        setWfConfigs]        = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [wfDirty,          setWfDirty]          = useState(false);
  const [savingConfig,     setSavingConfig]     = useState(false);
  const [newConfigName,    setNewConfigName]    = useState('');
  const [showSaveConfig,   setShowSaveConfig]   = useState(false);

  const DEFAULT_SCHED = {
    enabled: false, frequency: 'monthly', day_of_week: 0, day_of_month: 1,
    run_hour_utc: 18, training_days: 90, rank_by: 'calmar', top_k: 1,
  };
  const [schedule,    setSchedule]    = useState(null);
  const [schedDraft,  setSchedDraft]  = useState(DEFAULT_SCHED);
  const [savingSched, setSavingSched] = useState(false);
  const [triggering,  setTriggering]  = useState(false);
  const [reRankRuns,  setReRankRuns]  = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/helms/${id}`),
      api.get(`/helms/${id}/screeners`),
      api.get('/screeners'),
      api.get(`/helms/${id}/trade-configs`),
      api.get(`/helms/${id}/wf-runs`),
      api.get(`/helms/${id}/schedule`).catch(() => null),
      api.get(`/helms/${id}/runs`).catch(() => null),
      api.get(`/helms/${id}/pair-exclusions`).catch(() => ({ data: [] })),
      api.get('/wf-configs').catch(() => ({ data: [] })),
    ]).then(([h, s, all, tc, r, sched, runs, excl, cfgs]) => {
      setHelm(h.data);
      setScreeners(s.data);
      setAllScreeners(all.data);
      setTradeConfigs(tc.data);
      setWfRuns(r.data);
      setPairExclusions(excl.data);
      setWfConfigs(cfgs.data);
      if (sched) { setSchedule(sched.data); setSchedDraft(sched.data); }
      if (runs)  setReRankRuns(runs.data);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  // ── Screener handlers ────────────────────────────────────────────────────

  const handleAddScreener = async (e) => {
    e.preventDefault();
    if (!newScreenerId) return;
    setAddingScreener(true);
    try {
      const res = await api.post(`/helms/${id}/screeners`, {
        screener_id: parseInt(newScreenerId, 10),
        label: newScreenerLabel.trim() || undefined,
      });
      const full = allScreeners.find(s => s.id === res.data.screener_id);
      setScreeners(prev => [...prev, { ...res.data, screener_name: full?.name, file_path: full?.file_path }]);
      setNewScreenerId('');
      setNewScreenerLabel('');
      setShowAddScreener(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add screener');
    } finally {
      setAddingScreener(false);
    }
  };

  const handleRemoveScreener = async (sid) => {
    if (!(await confirm('This will remove the screener from this helm.', {
      title: 'Remove screener?', confirmText: 'Remove',
    }))) return;
    await api.delete(`/helms/${id}/screeners/${sid}`);
    setScreeners(prev => prev.filter(s => s.id !== sid));
    setPairExclusions(prev => prev.filter(e => e.helm_candidate_id !== sid));
  };

  // ── Trade config handlers ────────────────────────────────────────────────

  const handleAddTC = async (payload) => {
    setSavingTC(true);
    try {
      const res = await api.post(`/helms/${id}/trade-configs`, payload);
      setTradeConfigs(prev => [...prev, res.data]);
      setShowAddTC(false);
    } finally {
      setSavingTC(false);
    }
  };

  const handleUpdateTC = async (payload) => {
    setSavingTC(true);
    try {
      const res = await api.patch(`/helms/${id}/trade-configs/${editingTC.id}`, payload);
      setTradeConfigs(prev => prev.map(tc => tc.id === editingTC.id ? res.data : tc));
      setEditingTC(null);
    } finally {
      setSavingTC(false);
    }
  };

  const handleRemoveTC = async (tcId) => {
    if (!(await confirm('This will remove the trade config from this helm.', {
      title: 'Remove trade config?', confirmText: 'Remove',
    }))) return;
    await api.delete(`/helms/${id}/trade-configs/${tcId}`);
    setTradeConfigs(prev => prev.filter(tc => tc.id !== tcId));
    setPairExclusions(prev => prev.filter(e => e.trade_config_id !== tcId));
  };

  // ── Pair exclusion handler ───────────────────────────────────────────────

  const isPairExcluded = (candId, tcId) =>
    pairExclusions.some(e => e.helm_candidate_id === candId && e.trade_config_id === tcId);

  const handleTogglePair = async (candId, tcId, currentlyExcluded) => {
    const next = !currentlyExcluded;
    try {
      const res = await api.put(`/helms/${id}/pair-exclusions`, {
        helm_candidate_id: candId, trade_config_id: tcId, excluded: next,
      });
      if (res.data?.default_cleared) {
        setHelm(prev => ({ ...prev, default_candidate_id: null, default_trade_config_id: null }));
      }
      setPairExclusions(prev => next
        ? [...prev, { helm_candidate_id: candId, trade_config_id: tcId }]
        : prev.filter(e => !(e.helm_candidate_id === candId && e.trade_config_id === tcId)));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update pair exclusion');
    }
  };

  // ── Selection policy + fallback pair ─────────────────────────────────────

  const handleSetRefusePolicy = async (enabled) => {
    setSavingPolicy(true);
    try {
      const res = await api.patch(`/helms/${id}`, { refuse_nonpositive_winner: enabled });
      setHelm(prev => ({ ...prev, refuse_nonpositive_winner: res.data.refuse_nonpositive_winner }));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update selection policy');
    } finally {
      setSavingPolicy(false);
    }
  };


  const isPairDefault = (candId, tcId) =>
    helm?.default_candidate_id === candId && helm?.default_trade_config_id === tcId;

  const handleSetDefaultPair = async (candId, tcId, currentlyDefault) => {
    // Clicking the current fallback clears it; one fallback per helm.
    const body = currentlyDefault
      ? { helm_candidate_id: null, trade_config_id: null }
      : { helm_candidate_id: candId, trade_config_id: tcId };
    try {
      const res = await api.put(`/helms/${id}/default-pair`, body);
      setHelm(prev => ({
        ...prev,
        default_candidate_id: res.data.default_candidate_id,
        default_trade_config_id: res.data.default_trade_config_id,
      }));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to set fallback pair');
    }
  };

  const handleGenerateGrid = async (payload) => {
    setSavingGrid(true);
    try {
      const res = await api.post(`/helms/${id}/trade-configs/bulk-generate`, payload);
      setTradeConfigs(prev => [...prev, ...res.data.created]);
      setShowGridForm(false);
    } catch (e) {
      throw new Error(e.response?.data?.error || 'Failed to generate grid');
    } finally {
      setSavingGrid(false);
    }
  };

  // ── Saved run configs ────────────────────────────────────────────────────

  // Any edit to the form breaks the link to the saved config it came from.
  const setWf = (key, val) => {
    setWfForm(p => ({ ...p, [key]: val }));
    setWfDirty(true);
  };

  const wfPayload = () => ({
    wf_start:      wfForm.wf_start,
    wf_end:        wfForm.wf_end,
    training_days: parseInt(wfForm.training_days, 10),
    interval_days: parseInt(wfForm.interval_days, 10),
    top_k:         1,   // WF engine is winner-take-all; see the disabled field above
    initial_funds: parseFloat(wfForm.initial_funds),
    rank_by:       wfForm.rank_by,
    recency_alpha: wfForm.recency_alpha ?? 0.0,
  });

  const handleSelectConfig = (val) => {
    setSelectedConfigId(val);
    setShowSaveConfig(false);
    setError('');
    if (!val) { setWfDirty(false); return; }
    const cfg = wfConfigs.find(c => String(c.id) === String(val));
    if (!cfg) return;
    setWfForm(p => ({ ...p, ...cfg.config }));
    setWfDirty(false);
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    const name = newConfigName.trim();
    if (!name) return;
    setSavingConfig(true); setError('');
    try {
      const res = await api.post('/wf-configs', { name, config: wfPayload() });
      // The backend upserts on name, so replace any same-name entry.
      setWfConfigs(prev => [res.data, ...prev.filter(c => c.id !== res.data.id)]);
      setSelectedConfigId(String(res.data.id));
      setWfDirty(false);
      setShowSaveConfig(false);
      setNewConfigName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save config');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleUpdateConfig = async () => {
    if (!selectedConfigId) return;
    setSavingConfig(true); setError('');
    try {
      const res = await api.patch(`/wf-configs/${selectedConfigId}`, { config: wfPayload() });
      setWfConfigs(prev => prev.map(c => c.id === res.data.id ? { ...c, ...res.data } : c));
      setWfDirty(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update config');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!selectedConfigId) return;
    if (!(await confirm('Past runs keep their config label, but it can no longer be selected.', {
      title: 'Delete saved config?', confirmText: 'Delete',
    }))) return;
    try {
      await api.delete(`/wf-configs/${selectedConfigId}`);
      setWfConfigs(prev => prev.filter(c => String(c.id) !== String(selectedConfigId)));
      setSelectedConfigId('');
      setWfDirty(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete config');
    }
  };

  // ── Walk-forward run handler ─────────────────────────────────────────────

  const handleRun = async (e) => {
    e.preventDefault();
    setRunning(true); setError('');
    try {
      const res = await api.post(`/helms/${id}/wf-run`, {
        ...wfPayload(),
        // Only tag the run when the form still matches the saved config —
        // otherwise the comparison would group runs that weren't alike.
        run_config_id: (selectedConfigId && !wfDirty) ? parseInt(selectedConfigId, 10) : undefined,
      });
      // Backend starts the run in the background and returns immediately —
      // navigate straight to the result page, which polls for live progress.
      navigate(`/helms/${id}/wf-runs/${res.data.wf_run_id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Run failed';
      setError(err.response?.status === 409 ? `⚠ ${msg}` : msg);
    } finally {
      setRunning(false);
    }
  };

  const readOnly = helm && helm.is_global && helm.is_owner === false && !isSuperadmin;
  const canRun = screeners.length > 0 && tradeConfigs.length > 0;

  // ── Schedule handlers ────────────────────────────────────────────────────

  const handleRename = async (e) => {
    e.preventDefault();
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === helm.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await api.patch(`/helms/${id}`, { name: trimmed, description: helm.description });
      setHelm(h => ({ ...h, name: trimmed }));
      setEditingName(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename');
    } finally {
      setSavingName(false);
    }
  };

  const setSched = (key, val) => setSchedDraft(p => ({ ...p, [key]: val }));

  const handleSaveSchedule = async (e) => {
    e.preventDefault();
    setSavingSched(true);
    try {
      const res = await api.put(`/helms/${id}/schedule`, schedDraft);
      setSchedule(res.data); setSchedDraft(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSavingSched(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await api.post(`/helms/${id}/schedule/trigger`);
      const [schedRes, runsRes] = await Promise.all([
        api.get(`/helms/${id}/schedule`).catch(() => null),
        api.get(`/helms/${id}/runs`).catch(() => null),
      ]);
      if (schedRes) { setSchedule(schedRes.data); setSchedDraft(schedRes.data); }
      if (runsRes)  setReRankRuns(runsRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to trigger run');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex flex-col md:flex-row md:h-[calc(100vh-3rem)]">
        <div className="w-full md:w-44 flex-none border-b md:border-b-0 md:border-r border-border p-3 flex flex-row md:flex-col gap-1">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded" />)}
        </div>
        <div className="flex-1 p-6 flex flex-col gap-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}
          </div>
          <Skeleton className="h-32 w-full mt-2 rounded" />
        </div>
      </div>
    </AppLayout>
  );

  return (
    <AppLayout backTo="/helms" backLabel="← Helms" title={helm?.name}>
      <div className="flex flex-col md:flex-row md:h-[calc(100vh-3rem)]">

        {/* ── Sidebar ── */}
        <nav className="w-full md:w-44 flex-none border-b md:border-b-0 md:border-r border-border bg-surface-base flex flex-row md:flex-col overflow-x-auto no-scrollbar md:py-2">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`text-left px-4 py-2.5 text-sm transition-colors ${activeTab === t.key ? 'text-primary bg-primary/10 font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover'}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

          {/* ── Tab: Config ── */}
          {activeTab === 'config' && (
            <div className="flex flex-col gap-6 max-w-3xl">

              {/* Helm name */}
              <div className="flex items-center gap-2">
                {editingName ? (
                  <form onSubmit={handleRename} className="flex items-center gap-2 flex-1">
                    <Input
                      autoFocus
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      className="h-8 text-sm font-semibold max-w-xs"
                      disabled={savingName}
                    />
                    <Button type="submit" size="sm" className="h-8 px-2" disabled={savingName}>
                      {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingName(false)} disabled={savingName}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </form>
                ) : (
                  <>
                    <span className="text-base font-semibold text-foreground">{helm.name}</span>
                    {helm.is_global && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                        <Globe className="h-2.5 w-2.5" /> Global
                      </span>
                    )}
                    {!readOnly && (
                      <Button
                        size="sm" variant="ghost" className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
                        onClick={() => { setNameInput(helm.name); setEditingName(true); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Screeners */}
              <Section
                title="Screeners"
                action={!readOnly && !showAddScreener && (
                  <Button size="sm" onClick={() => setShowAddScreener(true)}>+ Add Screener</Button>
                )}
              >
                {showAddScreener && (
                  <form onSubmit={handleAddScreener} className="border border-border rounded-lg p-4 bg-surface-card flex flex-col gap-3 mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FieldRow label="Screener">
                        <NativeSelect value={newScreenerId} onChange={e => setNewScreenerId(e.target.value)} >
                          <option value="">— select —</option>
                          {allScreeners.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </NativeSelect>
                      </FieldRow>
                      <FieldRow label="Label (optional)">
                        <Input type="text" placeholder="e.g. GoldenCross v2" value={newScreenerLabel} onChange={e => setNewScreenerLabel(e.target.value)} />
                      </FieldRow>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setShowAddScreener(false); setNewScreenerId(''); setNewScreenerLabel(''); }}>Cancel</Button>
                      <Button type="submit" size="sm" disabled={addingScreener}>{addingScreener ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Adding…</> : 'Add'}</Button>
                    </div>
                  </form>
                )}

                {screeners.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">No screeners yet. Add at least one screener.</p>
                ) : (
                  <>
                    <DataTable headers={['#', 'Screener', 'Label', '']}>
                      {screeners.map((s, i) => (
                        <tr key={s.id} className="border-b border-border/40 hover:bg-surface-hover transition-colors">
                          <td className="px-3 py-2 text-sm text-muted-foreground">{i + 1}</td>
                          <td className="px-3 py-2 text-sm font-medium text-foreground">{s.screener_name}</td>
                          <td className="px-3 py-2 text-sm text-muted-foreground">{s.label || '—'}</td>
                          <td className="px-3 py-2">
                            {!readOnly && <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7" onClick={() => handleRemoveScreener(s.id)}>Remove</Button>}
                          </td>
                        </tr>
                      ))}
                    </DataTable>
                  </>
                )}
              </Section>

              {/* Trade Configs */}
              <Section
                title="Trade Configs"
                action={!readOnly && !showAddTC && !editingTC && !showGridForm && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowGridForm(true)}>Generate Grid</Button>
                    <Button size="sm" onClick={() => setShowAddTC(true)}>+ Add Trade Config</Button>
                  </div>
                )}
              >
                {showAddTC && (
                  <TradeConfigForm initial={null} onSave={handleAddTC} onCancel={() => setShowAddTC(false)} saving={savingTC} />
                )}
                {editingTC && (
                  <TradeConfigForm initial={editingTC} onSave={handleUpdateTC} onCancel={() => setEditingTC(null)} saving={savingTC} />
                )}
                {showGridForm && (
                  <TradeConfigGridForm onSave={handleGenerateGrid} onCancel={() => setShowGridForm(false)} saving={savingGrid} />
                )}

                {tradeConfigs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">No trade configs yet. Add at least one trade config.</p>
                ) : (
                  <>
                  {/* DESKTOP: Standard Table */}
                    <div className="hidden md:block">
                      <DataTable headers={['#', 'Name', 'Config', '']}>
                        {tradeConfigs.map((tc, i) => (
                          <tr key={tc.id} className="border-b border-border/40 hover:bg-surface-hover transition-colors">
                            <td className="px-3 py-2 text-sm text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2 text-sm font-medium text-foreground">{tc.name}</td>
                            <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{configSummary(tc.config)}</td>
                            <td className="px-3 py-2">
                              {!readOnly && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-7" onClick={() => { setShowAddTC(false); setEditingTC(tc); }}>Edit</Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleRemoveTC(tc.id)}>Remove</Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </DataTable>
                    </div>
                    {/* MOBILE: Card List */}
                    <div className="flex flex-col gap-3 md:hidden">
                      {tradeConfigs.map((tc, i) => (
                        <div key={tc.id} className="border border-border rounded-lg p-3 bg-surface-card flex flex-col gap-2">
                          {/* Card Header */}
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                              <span className="text-sm font-semibold text-foreground">{tc.name}</span>
                            </div>
                            {/* Actions */}
                            {!readOnly && (
                              <div className="flex gap-1 shrink-0">
                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setShowAddTC(false); setEditingTC(tc); }}>Edit</Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive bg-destructive/10 hover:bg-destructive/20" onClick={() => handleRemoveTC(tc.id)}>Del</Button>
                              </div>
                            )}
                          </div>
                          {/* Card Body */}
                          <div className="text-xs font-mono text-muted-foreground bg-surface-hover rounded p-2 overflow-x-auto no-scrollbar">
                            {configSummary(tc.config)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Section>

              {/* Pair Exclusions */}
              {screeners.length > 0 && tradeConfigs.length > 0 && (
                <Section title="Pair Exclusions">
                  <p className="text-xs text-muted-foreground -mt-1">
                    Every screener is ranked against every trade config by default. Uncheck a cell to exclude that specific combination.
                  </p>
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-border bg-surface-card cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!helm?.refuse_nonpositive_winner}
                      disabled={readOnly || savingPolicy}
                      onChange={e => handleSetRefusePolicy(e.target.checked)}
                    />
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-foreground">
                        Sit out when no pair qualifies
                      </span>
                      <span className="text-xs text-muted-foreground">
                        When the top-ranked combination scores zero or below, treat it as no selection rather than
                        trading it anyway. Off by default: the top-ranked pair is traded even on a zero or negative
                        score — and when every pair ties at zero, &ldquo;top-ranked&rdquo; is just whichever came
                        first in the database.
                      </span>
                    </span>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    With that on, you can star one cell as the <strong className="text-foreground">fallback pair</strong>:
                    instead of sitting the whole re-rank interval out, the walk-forward trades that pair. Results mark
                    those segments as a fallback, never as a winner.
                  </p>
                  <PairMatrix
                    screeners={screeners}
                    tradeConfigs={tradeConfigs}
                    isExcluded={isPairExcluded}
                    onToggle={handleTogglePair}
                    readOnly={readOnly}
                    isDefault={isPairDefault}
                    onSetDefault={handleSetDefaultPair}
                    fallbackEnabled={!!helm?.refuse_nonpositive_winner}
                  />
                  {helm?.default_candidate_id == null && !!helm?.refuse_nonpositive_winner && (
                    <p className="text-[11px] text-muted-foreground">
                      No fallback set — refused intervals will take no new entries.
                    </p>
                  )}
                </Section>
              )}
            </div>
          )}

          {/* ── Tab: Backtesting ── */}
          {activeTab === 'backtesting' && (
            <div className="flex flex-col gap-6 max-w-3xl">
              <Section title="Walk-forward Backtest">
                {!canRun && (
                  <p className="text-sm text-amber-500 py-2">Add at least one screener and one trade config to run.</p>
                )}
                <form onSubmit={handleRun} className="flex flex-col gap-4">
                  {/* Saved config picker — running two helms from one saved
                      config is what makes their results comparable. */}
                  <div className="flex flex-col gap-2 p-3 rounded-lg border border-border bg-surface-card">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      <div className="flex-1">
                        <FieldRow label="Saved Config">
                          <NativeSelect value={selectedConfigId} onChange={e => handleSelectConfig(e.target.value)}>
                            <option value="">— New config —</option>
                            {wfConfigs.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.helm_count > 0 ? ` · ${c.run_count} run${c.run_count === 1 ? '' : 's'} / ${c.helm_count} helm${c.helm_count === 1 ? '' : 's'}` : ''}
                              </option>
                            ))}
                          </NativeSelect>
                        </FieldRow>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedConfigId && wfDirty && (
                          <Button type="button" variant="outline" size="sm" disabled={savingConfig} onClick={handleUpdateConfig}>
                            {savingConfig ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Update'}
                          </Button>
                        )}
                        <Button type="button" variant="outline" size="sm" onClick={() => { setShowSaveConfig(v => !v); setNewConfigName(''); }}>
                          Save as…
                        </Button>
                        {selectedConfigId && (
                          <>
                            <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/wf-configs/${selectedConfigId}`)}>
                              Compare runs
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={handleDeleteConfig}>
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {showSaveConfig && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          autoFocus
                          placeholder="Config name e.g. 3Y / 90d / Calmar"
                          value={newConfigName}
                          onChange={e => setNewConfigName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveConfig(e); }}
                        />
                        <Button type="button" size="sm" disabled={savingConfig || !newConfigName.trim()} onClick={handleSaveConfig}>
                          {savingConfig ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowSaveConfig(false)}>Cancel</Button>
                      </div>
                    )}

                    {selectedConfigId && wfDirty && (
                      <p className="text-xs text-amber-500">
                        Edited since loading — this run won't be tagged with the saved config. Update it or save as new to keep runs comparable.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldRow label="Start Date">
                      <Input type="date" value={wfForm.wf_start} onChange={e => setWf('wf_start', e.target.value)} required />
                    </FieldRow>
                    <FieldRow label="End Date">
                      <Input type="date" value={wfForm.wf_end} onChange={e => setWf('wf_end', e.target.value)} required />
                    </FieldRow>
                    <FieldRow label="Training Window (days)">
                      <Input type="number" min="30" value={wfForm.training_days} onChange={e => setWf('training_days', e.target.value)} />
                    </FieldRow>
                    <FieldRow label="Re-rank Interval (days)">
                      <Input type="number" min="1" value={wfForm.interval_days} onChange={e => setWf('interval_days', e.target.value)} />
                    </FieldRow>
                    <FieldRow label="Initial Funds (₹)">
                      <Input type="number" min="1" value={wfForm.initial_funds} onChange={e => setWf('initial_funds', e.target.value)} />
                    </FieldRow>
                    {/* The WF engine trades scored[0] only — it never reads
                        top_k. Shown disabled rather than removed so the field
                        does not look configurable while being ignored. */}
                    <FieldRow label="Top K">
                      <div className="flex flex-col gap-1">
                        <Input type="number" value={1} disabled readOnly />
                        <span className="text-[10px] text-muted-foreground">
                          Walk-forward trades one pair per segment
                        </span>
                      </div>
                    </FieldRow>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-2">Rank By</div>
                    <RankSelector value={wfForm.rank_by} onChange={v => setWf('rank_by', v)} name="wf_rank_by" />
                  </div>

                  <Button type="submit" disabled={running || !canRun} className="w-full sm:w-auto">
                    {running ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Starting…</> : `▶ Run Walk-forward (${screeners.length}s × ${tradeConfigs.length}c)`}
                  </Button>
                </form>
              </Section>

              {wfRuns.length > 0 && (
                <Section title="Walk-forward Runs">
                  {/* DESKTOP: Standard Table */}
                  <div className="hidden md:block">
                    <DataTable headers={['Date', 'Config', 'Period', 'Windows', 'Rank By', 'Status', '']}>
                      {wfRuns.map(r => {
                        const nWindows = Math.floor(((new Date(r.wf_end) - new Date(r.wf_start)) / 86400000 - r.training_days) / r.interval_days);
                        return (
                          <tr key={r.id} className="border-b border-border/40 hover:bg-surface-hover transition-colors">
                            <td className="px-3 py-2 text-sm">{fmtDate(r.created_at)}</td>
                            <td className="px-3 py-2 text-xs">
                              {r.run_config_name ? (
                                r.run_config_id ? (
                                  <button
                                    type="button"
                                    className="text-primary hover:underline"
                                    onClick={() => navigate(`/wf-configs/${r.run_config_id}`)}
                                  >
                                    {r.run_config_name}
                                  </button>
                                ) : <span className="text-muted-foreground">{r.run_config_name}</span>
                              ) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{fmtDate(r.wf_start)} → {fmtDate(r.wf_end)}</td>
                            <td className="px-3 py-2 text-sm text-muted-foreground">~{Math.max(nWindows, 0)}</td>
                            <td className="px-3 py-2 text-sm">{r.rank_by}</td>
                            <td className="px-3 py-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${r.status === 'completed' ? 'bg-win/10 text-win' : r.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-surface-card text-muted-foreground'}`}>
                                {r.status}
                              </span>
                              {r.status === 'running' && r.total_windows > 0 && (
                                <span className="ml-1.5 text-[10px] text-muted-foreground font-mono">
                                  {r.phase === 'ranking' ? 'ranking' : 'live'} {r.current_window}/{r.total_windows}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.status !== 'failed' && (
                                <Button size="sm" variant="ghost" className="h-7" onClick={() => navigate(`/helms/${id}/wf-runs/${r.id}`)}>
                                  {r.status === 'completed' ? 'View' : 'Watch'}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </DataTable>
                  </div>

                  {/* MOBILE: Card List */}
                  <div className="flex flex-col gap-3 md:hidden">
                    {wfRuns.map(r => {
                      const nWindows = Math.floor(((new Date(r.wf_end) - new Date(r.wf_start)) / 86400000 - r.training_days) / r.interval_days);
                      return (
                        <div key={r.id} className="border border-border rounded-lg p-3 bg-surface-card flex flex-col gap-3">
                          {/* Top row: Date & Status */}
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground font-medium">
                              {fmtDate(r.created_at)}
                              {r.run_config_name && <span className="ml-1.5 text-foreground">· {r.run_config_name}</span>}
                            </span>
                            <span className="flex items-center gap-1.5">
                              {r.status === 'running' && r.total_windows > 0 && (
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {r.phase === 'ranking' ? 'ranking' : 'live'} {r.current_window}/{r.total_windows}
                                </span>
                              )}
                              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${r.status === 'completed' ? 'bg-win/10 text-win' : r.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-surface-base text-muted-foreground border border-border'}`}>
                                {r.status}
                              </span>
                            </span>
                          </div>
                          
                          {/* Middle row: Stats Grid */}
                          <div className="grid grid-cols-2 gap-2 text-sm bg-surface-base border border-border rounded p-2">
                            <div className="flex flex-col col-span-2">
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground">Period</span>
                              <span className="font-mono text-xs text-foreground">{fmtDate(r.wf_start)} → {fmtDate(r.wf_end)}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground">Windows</span>
                              <span className="text-sm text-foreground">~{Math.max(nWindows, 0)}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground">Rank By</span>
                              <span className="text-sm text-foreground">{r.rank_by}</span>
                            </div>
                          </div>
                          
                          {/* Bottom row: Action */}
                          {r.status !== 'failed' && (
                            <Button size="sm" variant="outline" className="w-full mt-1" onClick={() => navigate(`/helms/${id}/wf-runs/${r.id}`)}>
                              {r.status === 'completed' ? 'View Results' : 'Watch Progress'}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}
            </div>
          )}

          {/* ── Tab: Schedule ── */}
          {activeTab === 'schedule' && (
            <div className="flex flex-col gap-6 max-w-3xl">
              {!readOnly && <Section title="Re-rank Schedule">
                <form onSubmit={handleSaveSchedule} className="flex flex-col gap-4">
                  {/* Enable toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={schedDraft.enabled}
                      onChange={e => setSched('enabled', e.target.checked)}
                      className="accent-primary"
                    />
                    <span className="text-sm text-foreground">
                      {schedDraft.enabled ? 'Enabled — auto re-rank is on' : 'Disabled'}
                    </span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldRow label="Frequency">
                      <NativeSelect value={schedDraft.frequency} onChange={e => setSched('frequency', e.target.value)}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </NativeSelect>
                    </FieldRow>

                    {schedDraft.frequency === 'weekly' && (
                      <FieldRow label="Day of Week">
                        <NativeSelect value={schedDraft.day_of_week ?? 0} onChange={e => setSched('day_of_week', parseInt(e.target.value, 10))}>
                          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </NativeSelect>
                      </FieldRow>
                    )}

                    {schedDraft.frequency === 'monthly' && (
                      <FieldRow label="Day of Month">
                        <Input type="number" min="1" max="28" value={schedDraft.day_of_month ?? 1} onChange={e => setSched('day_of_month', parseInt(e.target.value, 10))} />
                      </FieldRow>
                    )}

                    <FieldRow label="Run Hour (UTC)">
                      <Input type="number" min="0" max="23" value={schedDraft.run_hour_utc} onChange={e => setSched('run_hour_utc', parseInt(e.target.value, 10))} />
                    </FieldRow>

                    <FieldRow label="Training Window (days)">
                      <Input type="number" min="30" value={schedDraft.training_days} onChange={e => setSched('training_days', parseInt(e.target.value, 10))} />
                    </FieldRow>

                    <FieldRow label="Top K">
                      <Input type="number" min="1" value={schedDraft.top_k} onChange={e => setSched('top_k', parseInt(e.target.value, 10))} />
                    </FieldRow>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-2">Rank By</div>
                    <RankSelector value={schedDraft.rank_by} onChange={v => setSched('rank_by', v)} name="sched_rank_by" />
                  </div>

                  {schedule && (
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {schedule.last_run_at && <span>Last run: {fmtDate(schedule.last_run_at)}</span>}
                      {schedule.next_run_at && schedule.enabled && <span>Next run: {fmtDate(schedule.next_run_at)}</span>}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {!readOnly && (
                      <Button type="submit" size="sm" disabled={savingSched || !canRun}>
                        {savingSched ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : 'Save Schedule'}
                      </Button>
                    )}
                    {schedule && (
                      <Button type="button" variant="outline" size="sm" disabled={triggering || !canRun} onClick={handleTrigger}>
                        {triggering ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Launching…</> : '▶ Run Now'}
                      </Button>
                    )}
                  </div>
                  {!canRun && (
                    <p className="text-xs text-muted-foreground">Add screeners and trade configs before enabling the schedule.</p>
                  )}
                </form>
              </Section>}

              {reRankRuns.length > 0 && (
                <Section title="Re-rank History">
                  {/* DESKTOP: Standard Table */}
                  <div className="hidden md:block">
                    <DataTable headers={['Date', 'Status', 'Top Screener', 'Top Config', 'Score', 'Pair']}>
                      {reRankRuns.slice(0, 10).map(r => (
                        <tr key={r.id} className="border-b border-border/40 hover:bg-surface-hover transition-colors">
                          <td className="px-3 py-2 text-sm">{fmtDate(r.created_at)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${r.status === 'completed' ? 'bg-win/10 text-win' : r.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-surface-card text-muted-foreground'}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm">{r.top_screener_name ?? '—'}</td>
                          <td className="px-3 py-2 text-sm">{r.top_tc_name ?? '—'}</td>
                          <td className="px-3 py-2 text-sm font-mono">{r.top_score != null ? r.top_score.toFixed(3) : '—'}</td>
                          <td className="px-3 py-2">
                            {r.fallback_reason && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-orange-400/15 text-orange-600 mr-1" title={`Ranking refused — ${r.fallback_reason}. The starred fallback pair was promoted.`}>
                                Fallback
                              </span>
                            )}
                            {r.top_pair_changed === true && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-400/15 text-amber-600" title="The top-ranked (screener, trade config) pair changed vs. the previous re-rank">
                                Rotated
                              </span>
                            )}
                            {r.top_pair_changed === false && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-muted text-muted-foreground">Same</span>
                            )}
                            {r.top_pair_changed == null && <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </DataTable>
                  </div>

                  {/* MOBILE: Card List */}
                  <div className="flex flex-col gap-3 md:hidden">
                    {reRankRuns.slice(0, 10).map(r => (
                      <div key={r.id} className="border border-border rounded-lg p-3 bg-surface-card flex flex-col gap-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-foreground">{fmtDate(r.created_at)}</span>
                          <div className="flex items-center gap-1.5">
                            {r.fallback_reason && (
                              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded font-bold bg-orange-400/15 text-orange-600" title={r.fallback_reason}>Fallback</span>
                            )}
                            {r.top_pair_changed === true && (
                              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded font-bold bg-amber-400/15 text-amber-600">Rotated</span>
                            )}
                            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${r.status === 'completed' ? 'bg-win/10 text-win' : r.status === 'failed' ? 'bg-destructive/10 text-destructive' : 'bg-surface-base text-muted-foreground'}`}>
                              {r.status}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-xs text-muted-foreground">Screener</span>
                            <span className="font-medium">{r.top_screener_name ?? '—'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-xs text-muted-foreground">Config</span>
                            <span className="font-medium text-right max-w-[60%] truncate">{r.top_tc_name ?? '—'}</span>
                          </div>
                          <div className="flex justify-between text-sm mt-1 pt-2 border-t border-border/50">
                            <span className="text-xs text-muted-foreground">Score</span>
                            <span className="font-mono font-medium text-primary">{r.top_score != null ? r.top_score.toFixed(3) : '—'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
      {confirmDialog}
    </AppLayout>
  );
}
